import appConfig from '@/features/app/constants/app-config';
import canClientUseCookieTokenStorage from '@/features/auth/functions/canClientUseCookieTokenStorage';
import legacy_parseAndValidateAccessToken from '@/features/auth/functions/legacy_parseAndValidateAccessToken';
import useGetUserWithAccessToken from '@/features/auth/hooks/useGetUserWithAccessToken';
import { AuthState } from '@/features/auth/types/auth-state';
import { User } from '@/features/user/types/user';
import Cookies from 'js-cookie';
import { useCallback } from 'react';

/**
 * Load the user object by using token from storage
 * The caller must guarantee the token is still valid (via checking expiration date)
 *
 * @param useCookieTokenStorage
 * @param enableLegacyTokenStorage
 * @returns
 */
export default function useValidateAndGetUserFromStorage() {
  const getUserWithAccessToken = useGetUserWithAccessToken();

  return useCallback(
    async (useCookieTokenStorage = false, enableLegacyTokenStorage = false) => {
      // use the new cookie storage if enabled and the client is compatible
      if (useCookieTokenStorage && canClientUseCookieTokenStorage()) {
        return getUserWithAccessToken({ useCookieStorage: true }).then((user) => {
          if (!user)
            return {
              token: undefined,
              user: null,
              isAccessTokenStillValid: false,
              isRefreshTokenStillValid: false,
            } satisfies AuthState;

          return {
            token: undefined,
            user,
            isAccessTokenStillValid: true,
            isRefreshTokenStillValid: true,
          } satisfies AuthState;
        });
      }

      if (enableLegacyTokenStorage) {
        const auth_data = Cookies.get(appConfig.authTokenKey);

        if (!auth_data)
          return Promise.resolve({
            token: null,
            user: null,
            isAccessTokenStillValid: false,
            isRefreshTokenStillValid: false,
          });

        const maybeToken = legacy_parseAndValidateAccessToken(auth_data);
        if (!maybeToken)
          return Promise.resolve({
            token: null,
            user: null,
            isAccessTokenStillValid: false,
            isRefreshTokenStillValid: false,
          });

        return getUserWithAccessToken({
          useCookieStorage: false,
          token: maybeToken.value,
        }).then((user: User) => {
          if (!user)
            return {
              token: maybeToken,
              user: null,
              isAccessTokenStillValid: false,
              isRefreshTokenStillValid: false,
            } satisfies AuthState;

          return {
            token: {
              value: maybeToken.value,
              expiresAt: maybeToken.expiresAt,
              refreshToken: maybeToken.refreshToken,
              rememberMe: maybeToken.rememberMe,
              refreshTokenExpiresAt: maybeToken.refreshTokenExpiresAt,
            },
            user,
            isAccessTokenStillValid: true,
            isRefreshTokenStillValid: true,
          } satisfies AuthState;
        });
      }

      return Promise.resolve({
        token: useCookieTokenStorage ? undefined : null,
        user: null,
        isAccessTokenStillValid: false,
        isRefreshTokenStillValid: false,
      });
    },
    [getUserWithAccessToken]
  );
}
